/**
 * DiscountVoidScreen — margin given away as discounts and sales cancelled as
 * voids, both attributed to the biller. Staff oversight and margin-leak control.
 *
 * Computed entirely from local WatermelonDB — no network required.
 */

import { useCallback, useEffect, useState } from 'react';
import { Download, RefreshCw } from 'lucide-react';
import { useAppStore } from '@/store/useAppStore';
import { cn } from '@/utils/cn';
import { formatCurrency } from '@/utils/format';
import {
  presetRange,
  toDateInputValue,
  startOfDay,
  lastIncludedInstant,
  type RangePreset as Preset,
} from '@/utils/dateRange';
import { Button } from '@/components/ui/Button';
import { pdf, file } from '@/platform';
import { toCsv } from '@/utils/csv';
import { loadDiscountVoid } from '@/reports/discountVoid/discountVoidDataSource';
import { buildDiscountVoid, type DiscountVoidReport } from '@/reports/discountVoid/discountVoid';

const pct = (fraction: number) => `${(fraction * 100).toFixed(1)}%`;

// ─── Component ───────────────────────────────────────────────────────────────

export function DiscountVoidScreen() {
  const activeStoreId = useAppStore((s) => s.activeStoreId);
  const [preset, setPreset] = useState<Preset>('month');
  const [customFrom, setCustomFrom] = useState(toDateInputValue(startOfDay(new Date())));
  const [customTo, setCustomTo]     = useState(toDateInputValue(new Date()));
  const [data, setData]             = useState<DiscountVoidReport | null>(null);
  const [loading, setLoading]       = useState(true);
  const [exporting, setExporting]   = useState(false);

  const range = useCallback(
    (): [Date, Date] => presetRange(preset, { from: customFrom, to: customTo }),
    [preset, customFrom, customTo],
  );

  const load = useCallback(async () => {
    if (!activeStoreId) return;
    setLoading(true);
    try {
      const [from, to] = range();
      const source = await loadDiscountVoid(activeStoreId, from, to);
      setData(buildDiscountVoid(source));
    } finally {
      setLoading(false);
    }
  }, [activeStoreId, range]);

  useEffect(() => { void load(); }, [load]);

  const rangeStamp = (): string => {
    const [from, to] = range();
    return `${toDateInputValue(from)}_${toDateInputValue(lastIncludedInstant(to))}`;
  };

  const handleExportCsv = async () => {
    if (!data) return;
    setExporting(true);
    try {
      const csv = toCsv(data.voids, [
        { header: 'Invoice', value: (v) => v.invoiceNumber },
        { header: 'Date', value: (v) => toDateInputValue(v.date) },
        { header: 'Biller', value: (v) => v.staffName },
        { header: 'Value', value: (v) => v.value },
        { header: 'Reason', value: (v) => v.reason ?? '' },
      ]);
      await file.saveText({
        content: csv,
        defaultFileName: `Voids_${rangeStamp()}.csv`,
        filters: [{ name: 'CSV', extensions: ['csv'] }],
      });
    } finally {
      setExporting(false);
    }
  };

  const handleExportPdf = async () => {
    if (!data) return;
    setExporting(true);
    try {
      const [from, to] = range();
      await pdf.saveFromHtml({
        html: buildDiscountVoidHtml(data, from, to),
        defaultFileName: `DiscountVoid_${rangeStamp()}.pdf`,
      });
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="flex h-full flex-col gap-4 overflow-auto pb-6">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2">
        {(['today', 'week', 'month', 'custom'] as Preset[]).map((p) => (
          <button key={p} type="button" onClick={() => setPreset(p)}
            className={cn('rounded-md px-3 py-1.5 text-body transition-colors',
              preset === p ? 'bg-accent/10 text-accent-ink' : 'text-fg-secondary hover:bg-bg-subtle')}>
            {p === 'today' ? 'Today' : p === 'week' ? 'Last 7 days' : p === 'month' ? 'This month' : 'Custom'}
          </button>
        ))}
        {preset === 'custom' && (
          <div className="flex items-center gap-2">
            <input type="date" value={customFrom} max={customTo}
              onChange={(e) => setCustomFrom(e.target.value)}
              className="rounded-md border border-stroke-hairline/15 bg-bg-surface px-2 py-1 text-body text-fg-primary" />
            <span className="text-fg-tertiary">–</span>
            <input type="date" value={customTo} min={customFrom}
              onChange={(e) => setCustomTo(e.target.value)}
              className="rounded-md border border-stroke-hairline/15 bg-bg-surface px-2 py-1 text-body text-fg-primary" />
          </div>
        )}
        <div className="ml-auto flex items-center gap-2">
          <Button variant="secondary" onClick={() => void load()} disabled={loading}>
            <RefreshCw size={14} className={cn(loading && 'animate-spin')} />
          </Button>
          <Button variant="secondary" onClick={() => void handleExportCsv()} disabled={!data || exporting || data.voids.length === 0}>
            <Download size={14} className="mr-1.5" /> CSV
          </Button>
          <Button variant="secondary" onClick={() => void handleExportPdf()} disabled={!data || exporting}>
            <Download size={14} className="mr-1.5" /> PDF
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex flex-1 items-center justify-center text-body text-fg-tertiary">Loading…</div>
      ) : !data ? null : (
        <>
          {/* KPI row */}
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <KPI label="Discount given" value={formatCurrency(data.discountTotal)} sub={`${pct(data.overallDiscountPct)} of ticket`} warn={data.discountTotal > 0} />
            <KPI label="Discounted bills" value={String(data.discountedCount)} />
            <KPI label="Voided value" value={formatCurrency(data.voidValue)} sub={`${data.voidCount} bills`} warn={data.voidValue > 0} />
            <KPI label="Void rate" value={pct(data.voidRatePct)} warn={data.voidRatePct > 0} />
          </div>

          {/* Discount by staff */}
          {data.discountByStaff.length > 0 && (
            <Card title="Discounts by staff">
              <table className="w-full text-body">
                <thead>
                  <tr className="border-b border-stroke-hairline/10 text-label uppercase text-fg-tertiary">
                    <th className="pb-1 text-left font-normal">Staff</th>
                    <th className="pb-1 text-right font-normal">Bills</th>
                    <th className="pb-1 text-right font-normal">Discount</th>
                    <th className="pb-1 text-right font-normal">% of ticket</th>
                  </tr>
                </thead>
                <tbody>
                  {data.discountByStaff.map((s) => (
                    <tr key={s.staffId} className="border-b border-stroke-hairline/10">
                      <td className="py-1.5 text-fg-primary">{s.name}</td>
                      <td className="py-1.5 text-right tabular-nums text-fg-secondary">{s.invoiceCount}</td>
                      <td className="py-1.5 text-right tabular-nums text-fg-primary">{formatCurrency(s.discountTotal)}</td>
                      <td className={cn('py-1.5 text-right tabular-nums', s.discountPct >= 0.15 ? 'text-warning-ink' : 'text-fg-tertiary')}>
                        {pct(s.discountPct)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
          )}

          {/* Voids */}
          {data.voids.length === 0 ? (
            <div className="rounded-lg border border-stroke-hairline/10 bg-bg-surface px-4 py-8 text-center text-body text-fg-tertiary">
              No voided sales in this period.
            </div>
          ) : (
            <Card title="Voided invoices">
              {data.voidByStaff.length > 1 && (
                <div className="mb-3 flex flex-wrap gap-2">
                  {data.voidByStaff.map((s) => (
                    <span key={s.staffId} className="rounded-md bg-bg-subtle px-2 py-1 text-label text-fg-secondary">
                      {s.name}: {s.count} · {formatCurrency(s.value)}
                    </span>
                  ))}
                </div>
              )}
              <div className="overflow-x-auto">
                <table className="w-full min-w-[560px] text-body">
                  <thead>
                    <tr className="border-b border-stroke-hairline/10 text-label uppercase text-fg-tertiary">
                      <th className="pb-1 text-left font-normal">Invoice</th>
                      <th className="pb-1 text-left font-normal">Date</th>
                      <th className="pb-1 text-left font-normal">Biller</th>
                      <th className="pb-1 text-right font-normal">Value</th>
                      <th className="pb-1 text-left font-normal">Reason</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.voids.map((v) => (
                      <tr key={v.invoiceNumber} className="border-b border-stroke-hairline/10">
                        <td className="py-1.5 text-fg-primary tabular-nums">{v.invoiceNumber}</td>
                        <td className="py-1.5 text-fg-secondary tabular-nums">{v.date.toLocaleDateString('en-IN')}</td>
                        <td className="py-1.5 text-fg-secondary">{v.staffName}</td>
                        <td className="py-1.5 text-right tabular-nums text-warning-ink">{formatCurrency(v.value)}</td>
                        <td className="py-1.5 text-fg-tertiary">{v.reason ?? <span className="italic">no reason</span>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          )}
        </>
      )}
    </div>
  );
}

// ─── Small helpers ────────────────────────────────────────────────────────────

function KPI({ label, value, sub, warn }: { label: string; value: string; sub?: string; warn?: boolean }) {
  return (
    <div className="rounded-lg border border-stroke-hairline/10 bg-bg-surface px-4 py-3">
      <div className="text-label uppercase text-fg-tertiary">{label}</div>
      <div className={cn('mt-1 text-heading tabular-nums', warn ? 'text-warning-ink' : 'text-fg-primary')}>{value}</div>
      {sub && <div className="mt-0.5 text-label text-fg-tertiary">{sub}</div>}
    </div>
  );
}

function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-lg border border-stroke-hairline/10 bg-bg-surface px-4 py-3">
      <div className="mb-2 text-label uppercase text-fg-tertiary">{title}</div>
      {children}
    </div>
  );
}

// ─── PDF export ───────────────────────────────────────────────────────────────

function buildDiscountVoidHtml(d: DiscountVoidReport, from: Date, toExclusive: Date): string {
  const fmt = (n: number) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 2 }).format(n);
  const fmtDate = (dt: Date) => dt.toLocaleDateString('en-IN');
  const to = lastIncludedInstant(toExclusive);
  const esc = (s: string) => s.replace(/[&<>]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' })[c] as string);

  const discRows = d.discountByStaff.map((s) =>
    `<tr><td>${esc(s.name)}</td><td style="text-align:right">${s.invoiceCount}</td>
     <td style="text-align:right">${fmt(s.discountTotal)}</td><td style="text-align:right">${(s.discountPct * 100).toFixed(1)}%</td></tr>`,
  ).join('');

  const voidRows = d.voids.map((v) =>
    `<tr><td>${esc(v.invoiceNumber)}</td><td>${fmtDate(v.date)}</td><td>${esc(v.staffName)}</td>
     <td style="text-align:right">${fmt(v.value)}</td><td>${v.reason ? esc(v.reason) : '—'}</td></tr>`,
  ).join('');

  return `<!DOCTYPE html><html><head><meta charset="utf-8">
<style>body{font-family:sans-serif;margin:32px;color:#222}h1{font-size:22px;margin-bottom:2px}h2{font-size:13px;margin-top:22px;text-transform:uppercase;color:#666}
table{width:100%;border-collapse:collapse;font-size:12px;margin-top:8px}th,td{padding:5px 8px;border-bottom:1px solid #eee}
th{text-align:left;color:#666;font-weight:500}</style></head>
<body>
<h1>Discount &amp; Void Audit</h1>
<p style="color:#888">${fmtDate(from)} – ${fmtDate(to)} · Discount ${fmt(d.discountTotal)} (${(d.overallDiscountPct * 100).toFixed(1)}%) · Voided ${fmt(d.voidValue)} across ${d.voidCount} bills · Void rate ${(d.voidRatePct * 100).toFixed(1)}%</p>
${discRows ? `<h2>Discounts by staff</h2>
<table><thead><tr><th>Staff</th><th style="text-align:right">Bills</th><th style="text-align:right">Discount</th><th style="text-align:right">% of ticket</th></tr></thead><tbody>${discRows}</tbody></table>` : ''}
${voidRows ? `<h2>Voided invoices</h2>
<table><thead><tr><th>Invoice</th><th>Date</th><th>Biller</th><th style="text-align:right">Value</th><th>Reason</th></tr></thead><tbody>${voidRows}</tbody></table>` : ''}
<p style="margin-top:36px;font-size:11px;color:#aaa">Generated by BigxBook Desktop on ${new Date().toLocaleString('en-IN')}</p>
</body></html>`;
}
